"use client";

import type { Dispatch, SetStateAction } from "react";
import {
  TOOLS,
  FONT_CSS,
  GLYPH_LABEL,
  GlyphIcon,
  SIZE_LABEL,
  SIZE_PX,
  isMark,
  isMinimalBar,
  type FontFamily,
  type FontSize,
  type PlacedField,
  type InteractionMode,
} from "./fillAndSignShared";

/**
 * Fill & Sign chrome: the desktop tool rail, the mobile tool strip, and the
 * property bar for the selected field.
 *
 * Presentation only -- every gesture and history handler stays in
 * FillAndSignTool and arrives as a prop.
 *
 * Bodies relocated verbatim from FillAndSignTool.tsx (de-indented).
 */
export function LeftToolbar({
  mode, setMode, fields, past, future, undo, redo, reset, submit, processing,
}: {
  mode: InteractionMode;
  setMode: Dispatch<SetStateAction<InteractionMode>>;
  fields: PlacedField[];
  past: unknown[];
  future: unknown[];
  undo: () => void;
  redo: () => void;
  reset: () => void;
  submit: () => void | Promise<void>;
  processing: boolean;
}) {
  return (
    <div className="hidden sm:flex flex-col gap-1 shrink-0 sticky self-start" style={{ width: 36, top: 96 }}>
      {TOOLS.map((tool) => (
        <button
          key={tool.mode}
          onClick={() => setMode(mode === tool.mode ? "select" : tool.mode)}
          className={`relative flex items-center justify-center rounded-md transition-all ${
            mode === tool.mode
              ? "bg-[var(--color-ink)] text-white shadow-sm"
              : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          }`}
          style={{ width: 36, height: 32 }}
          title={`${tool.label} (${tool.key})`}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill={tool.iconFilled ? "currentColor" : "none"}
            stroke={tool.iconFilled ? "none" : "currentColor"}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d={tool.icon} />
          </svg>
          <span className={`absolute -top-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded text-[7px] font-bold leading-none ${mode === tool.mode ? "bg-white text-[var(--color-ink)]" : "bg-slate-200/80 text-slate-500"}`}>
            {tool.key}
          </span>
        </button>
      ))}

      <div className="my-1.5 border-t border-slate-200" />

      {/* Undo / redo */}
      <button
        onClick={undo}
        disabled={past.length === 0}
        className="flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400"
        style={{ width: 36, height: 32 }}
        title="Undo (Ctrl+Z)"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
        </svg>
      </button>

      <button
        onClick={redo}
        disabled={future.length === 0}
        className="flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400"
        style={{ width: 36, height: 32 }}
        title="Redo (Ctrl+Y)"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
        </svg>
      </button>

      <div className="my-1.5 border-t border-slate-200" />

      <button
        onClick={submit}
        disabled={processing || fields.length === 0}
        className="flex items-center justify-center rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ width: 36, height: 32 }}
        title="Download filled PDF"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
      </button>

      <button
        onClick={reset}
        className="flex items-center justify-center rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
        style={{ width: 36, height: 32 }}
        title="Start over"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
        </svg>
      </button>
    </div>
  );
}

export function MobileToolbar({
  mode, setMode, past, future, undo, redo,
}: {
  mode: InteractionMode;
  setMode: Dispatch<SetStateAction<InteractionMode>>;
  past: unknown[];
  future: unknown[];
  undo: () => void;
  redo: () => void;
}) {
  return (
    <div className="flex sm:hidden items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1.5">
      {TOOLS.map((tool) => (
        <button
          key={tool.mode}
          onClick={() => setMode(mode === tool.mode ? "select" : tool.mode)}
          aria-label={tool.label}
          title={tool.label}
          className={`flex-1 flex items-center justify-center rounded-md py-2 transition-all ${
            mode === tool.mode ? "bg-[var(--color-ink)] text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill={tool.iconFilled ? "currentColor" : "none"}
            stroke={tool.iconFilled ? "none" : "currentColor"}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d={tool.icon} />
          </svg>
        </button>
      ))}

      <div className="mx-0.5 h-5 w-px shrink-0 bg-slate-300/70" />

      <button
        onClick={undo}
        disabled={past.length === 0}
        aria-label="Undo"
        title="Undo"
        className="shrink-0 flex w-9 items-center justify-center rounded-md py-2 text-slate-500 hover:text-slate-700 transition-all disabled:opacity-30"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
        </svg>
      </button>

      <button
        onClick={redo}
        disabled={future.length === 0}
        aria-label="Redo"
        title="Redo"
        className="shrink-0 flex w-9 items-center justify-center rounded-md py-2 text-slate-500 hover:text-slate-700 transition-all disabled:opacity-30"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
        </svg>
      </button>
    </div>
  );
}

export function PropertyBar({
  mode, modeHints, selectedField,
  setFields, setSelectedId, setEditingId, updateField, snapshot,
  renderColorPicker,
}: {
  mode: InteractionMode;
  modeHints: Record<InteractionMode, string>;
  selectedField: PlacedField | undefined;
  setFields: Dispatch<SetStateAction<PlacedField[]>>;
  setSelectedId: Dispatch<SetStateAction<string | null>>;
  setEditingId: Dispatch<SetStateAction<string | null>>;
  updateField: (id: string, patch: Partial<PlacedField>) => void;
  snapshot: () => void;
  renderColorPicker: (field: PlacedField) => React.ReactNode;
}) {
  return (
    <div className="sticky z-20 -mx-1 px-1" style={{ top: 72 }}>
      {/* Mode hint */}
      <p className="text-xs text-slate-400 text-center mb-2">{modeHints[mode]}</p>

      {/* Property bar for selected field */}
      {selectedField && (
        <div className="flex items-center gap-1.5 rounded-xl bg-[var(--color-ink)] px-3 py-2 shadow-lg shadow-slate-900/10 mb-2">
          {isMinimalBar(selectedField.type) ? (
            /* Signature / marks / whiteout: no font/size — label + resize hint (+ color for marks) */
            <>
            <div className="flex items-center gap-2 px-1">
              <GlyphIcon type={selectedField.type} className="h-4 w-4 text-[var(--color-gold)] shrink-0" />
              <span className="text-[11px] font-medium text-slate-200">{GLYPH_LABEL[selectedField.type]}</span>
              <span className="hidden sm:inline text-[10px] text-slate-400">Drag the corner handle to resize</span>
            </div>
            {isMark(selectedField.type) && (
              <>
                <div className="h-5 border-l border-white/15" />
                {renderColorPicker(selectedField)}
              </>
            )}
            </>
          ) : (
          <>
          {/* Font group */}
          <div className="flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1">
            <svg className="h-3.5 w-3.5 text-slate-300 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.243 4.493v7.5m0 0v7.502m0-7.501h10.5m0-7.5v7.5m0 0v7.501" />
            </svg>
            {(["sans", "serif", "mono"] as FontFamily[]).map((font) => (
              <button
                key={font}
                onClick={() => { snapshot(); updateField(selectedField.id, { font }); }}
                className={`rounded-md px-2.5 py-0.5 text-[11px] font-medium transition-all ${
                  selectedField.font === font
                    ? "bg-[var(--color-gold)] text-[var(--color-ink)] shadow-sm"
                    : "text-slate-300 hover:text-white hover:bg-white/10"
                }`}
                style={{ fontFamily: FONT_CSS[font] }}
              >
                {font === "sans" ? "Sans" : font === "serif" ? "Serif" : "Mono"}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="h-5 border-l border-white/15" />

          {/* Size group */}
          <div className="flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1">
            <svg className="h-3.5 w-3.5 text-slate-300 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            </svg>
            {(["small", "medium", "large"] as FontSize[]).map((size) => (
              <button
                key={size}
                onClick={() => { snapshot(); updateField(selectedField.id, { size }); }}
                className={`rounded-md px-2 py-0.5 text-[11px] font-bold tabular-nums transition-all ${
                  selectedField.size === size && !selectedField.customPx
                    ? "bg-[var(--color-gold)] text-[var(--color-ink)] shadow-sm"
                    : "text-slate-300 hover:text-white hover:bg-white/10"
                }`}
              >
                {SIZE_LABEL[size]}
              </button>
            ))}
            <input
              type="number"
              min={6}
              max={72}
              value={selectedField.customPx || SIZE_PX[selectedField.size]}
              onChange={(e) => {
                const px = Math.max(6, Math.min(72, parseInt(e.target.value) || 14));
                updateField(selectedField.id, { customPx: px });
              }}
              className={`w-11 rounded-md border text-center text-[11px] font-medium tabular-nums py-0.5 outline-none transition-all ${
                selectedField.customPx
                  ? "border-[var(--color-gold)] bg-[var(--color-gold)]/20 text-[var(--color-gold)]"
                  : "border-white/20 bg-white/5 text-slate-300 focus:border-[var(--color-gold)]"
              }`}
              title="Custom font size (px)"
            />
            <span className="text-[10px] text-slate-400">px</span>
          </div>

          {/* Divider */}
          <div className="h-5 border-l border-white/15" />

          {/* Color */}
          {renderColorPicker(selectedField)}
          </>
          )}

          <div className="flex-1" />

          {/* Delete (shared — all field types) */}
          <button
            onClick={() => { snapshot(); setFields((p) => p.filter((f) => f.id !== selectedField.id)); setSelectedId(null); setEditingId(null); }}
            className="rounded-lg p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/15 transition-all"
            title="Delete field"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
